import ProForm, {ProFormFieldProps} from '@ant-design/pro-form';
import {Input, InputProps} from 'antd';
import {padEnd} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';

type CommonTimeInputProps = Omit<InputProps, 'onChange'> & {
  onChange?: (time: string) => void;
};

/** 全形數字／冒號 → 半形，避免中文 IME 全形模式讓 parseInt 失效 */
export const normalizeFullWidthTimeChars = (value: string): string => {
  return value
    .replace(/[０-９]/g, ch =>
      String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30)
    )
    .replace(/：/g, ':');
};

/** 驗證並格式化 HH:mm 片段；非法時回傳 null（不回寫表單） */
export const formatTimeInput = (value: string): string | null => {
  try {
    let result = '';
    const [h, m] = value.split(':');
    if (h) {
      if (h.length === 1 && parseInt(h, 10) > 2) {
        throw new Error();
      } else if (h.length === 2 && parseInt(h, 10) > 23) {
        throw new Error();
      } else {
        result += h;
        if (h.length === 2) {
          result += ':';
        }
      }
    }

    if (m) {
      if (
        (m.length === 1 && parseInt(m, 10) > 6) ||
        (m.length === 2 && parseInt(m, 10) > 60)
      ) {
        throw new Error();
      }
      result += m.slice(0, 2);
    }
    return result;
  } catch {
    return null;
  }
};

export const CommonTimeField = (
  props: ProFormFieldProps<CommonTimeInputProps>
) => {
  return (
    <ProForm.Item {...(props as any)}>
      <CommonTimeInput {...props.fieldProps} />
    </ProForm.Item>
  );
};

export const CommonTimeInput = ({
  value: prevValue,
  onChange,
  ...props
}: CommonTimeInputProps) => {
  const [draft, setDraft] = useState(
    typeof prevValue === 'string' ? prevValue : ''
  );
  const composingRef = useRef(false);

  // 外部 value（reset／initialValue）變更且非組字時同步 draft
  useEffect(() => {
    if (!composingRef.current) {
      setDraft(typeof prevValue === 'string' ? prevValue : '');
    }
  }, [prevValue]);

  const commitFormatted = (raw: string) => {
    const normalized = normalizeFullWidthTimeChars(raw);
    const current = typeof prevValue === 'string' ? prevValue : '';

    // 刪短：直接回寫，不做格式擴充（避免與刪除操作打架）
    if (current.length > normalized.length) {
      setDraft(normalized);
      if (normalized !== current) {
        onChange?.(normalized);
      }
      return;
    }

    const formatted = formatTimeInput(normalized);
    if (formatted === null) {
      // 非法：只更新畫面草稿，不回寫表單
      setDraft(normalized);
      return;
    }

    setDraft(formatted);
    // 冪等：值未變則略過（涵蓋 Windows compositionend 後尾隨 input）
    if (formatted !== current) {
      onChange?.(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={draft}
      placeholder="HH:mm"
      onCompositionStart={() => {
        composingRef.current = true;
      }}
      onCompositionEnd={e => {
        composingRef.current = false;
        commitFormatted(e.currentTarget.value);
      }}
      onBlur={e => {
        const raw =
          typeof e.target.value === 'string' ? e.target.value : draft;
        const normalized = normalizeFullWidthTimeChars(raw);
        const [h, m = ''] = normalized.split(':');
        if (h && h.length === 2) {
          const next = `${h}:${padEnd(m, 2, '0')}`;
          setDraft(next);
          if (next !== prevValue) {
            onChange?.(next);
          }
        } else {
          setDraft('');
          if (prevValue) {
            onChange?.('');
          }
        }
      }}
      onChange={e => {
        if (typeof e.target.value !== 'string') {
          return;
        }
        const value = e.target.value;
        // 組字中：只更新 draft，不轉換、不回寫表單，避免改寫 DOM
        if (composingRef.current) {
          setDraft(value);
          return;
        }
        commitFormatted(value);
      }}
    />
  );
};
